import { useCallback, useMemo } from "react"

import { QueryClient, useQueryClient } from "@tanstack/react-query"
import { type NavigateFunction, useNavigate } from "react-router-dom"

import { type TButtonProps } from "@/3514/components/shared/button/button.component"
import { useIsNetworkFast } from "@/3514/hooks"
import { coachingSessionsIndividualQueryPrefetcher, participantByIdQueryPrefetcher } from "@/3514/queries"
import { type TModalDispatcher, useModalDispatcher } from "@/3514/store/modal"
import { ESessionModality, EUserRole } from "@/3514/types"
import { type IUseTranslation, useTranslation } from "@/hooks"

import actionPropsGetterMap from "./session-info.config.actions"
import buttonPropsGetterMap from "./session-info.config.buttons"
import messagePropsGetter from "./session-info.config.messages"
import type {
    TSelectInfoDropdownItem as TDropdownItem,
    _TSessionInfoMessageComponentProps as TMessageProps,
    TSessionInfoRenderActionsPropsFunction as TRenderActionsPropsFunction,
    TSessionInfoRenderButtonPropsFunction as TRenderButtonPropsFunction,
    _TSessionInfoRenderButtonPropsFunctionInternalParams as TRenderButtonPropsRenderFunctionInternalParams,
    _TSessionInfoRenderFunctionSharedInternalParams as TRenderFUnctionSharedInternalParams,
    _TSessionInfoRenderFunctionSharedExternalParams as TRenderFunctionSharedExternalParams,
    TSessionInfoRenderMessagesPropsFunction as TRenderMessageBusPropsFunction,
    _TSessionInfoRenderMessagesPropsFunctionInternalParams as TRenderMessagePropsFunctionInternalParams
} from "./session-info.types"

function useSessionInfoRenderPropsFunctions(sessionViewerRole: EUserRole): {
    renderButtonsProps: TRenderButtonPropsFunction
    renderMessagesProps: TRenderMessageBusPropsFunction
    renderActionsProps: TRenderActionsPropsFunction
} {
    const modalDispatcher: TModalDispatcher = useModalDispatcher()

    const qc: QueryClient = useQueryClient()

    const navigate: NavigateFunction = useNavigate()

    const { t }: IUseTranslation = useTranslation()

    const isNetworkFast: boolean = useIsNetworkFast()

    const sharedExternal: TRenderFunctionSharedExternalParams = useMemo(
        (): TRenderFunctionSharedExternalParams => ({
            modalDispatcher,
            t,
            qc,
            navigate,
            isNetworkFast
        }),
        [modalDispatcher, t, qc, navigate, isNetworkFast]
    )

    const renderButtonsProps: TRenderButtonPropsFunction = useCallback<TRenderButtonPropsFunction>(
        (internal: TRenderButtonPropsRenderFunctionInternalParams): TButtonProps[] =>
            buttonPropsGetterMap[sessionViewerRole](internal, sharedExternal),
        [sharedExternal, sessionViewerRole]
    )

    const renderActionsProps: TRenderActionsPropsFunction = useCallback<TRenderActionsPropsFunction>(
        (internal: TRenderFUnctionSharedInternalParams): TDropdownItem[] =>
            actionPropsGetterMap[sessionViewerRole](internal, sharedExternal),
        [sharedExternal, sessionViewerRole]
    )

    const renderMessagesProps: TRenderMessageBusPropsFunction = useCallback<TRenderMessageBusPropsFunction>(
        (internal: TRenderMessagePropsFunctionInternalParams): TMessageProps[] =>
            messagePropsGetter(internal, sharedExternal),
        [sharedExternal]
    )

    return { renderButtonsProps, renderActionsProps, renderMessagesProps }
}

function useSessionInfoAttendeeListPrefetcher(): (
    participantId: number,
    sessionModality: ESessionModality
) => Promise<void> {
    const qc: QueryClient = useQueryClient()

    return useCallback(
        async (participantId: number, sessionModality: ESessionModality): Promise<void> => {
            if (sessionModality === ESessionModality.Individual) return

            void participantByIdQueryPrefetcher({ qc }, { participantId })
            void coachingSessionsIndividualQueryPrefetcher({ qc }, { participantId })
        },
        [qc]
    )
}

export { useSessionInfoRenderPropsFunctions, useSessionInfoAttendeeListPrefetcher }
